import React, {
  createContext,
  useReducer,
  useContext,
  useEffect,
  useCallback,
} from "react";
import { getItem, setItem } from "utils/CommonUtils";
import { useMutation } from "react-fetching-library";
import { USER_TYPE } from "helpers/interfaces";
import { isSuccess, successData, errorMessage } from "utils/ServiceAPIUtil";

export interface UserState {
  userType: USER_TYPE;
  error?: Error;
  accessToken?: string;
  accountProfile?: any;
  OTPLogin: ({ phoneNumber }: { phoneNumber: string }) => Promise<any>;
  OTPVerification: ({
    phoneNumber,
    otpCode,
  }: {
    phoneNumber: string;
    otpCode: string;
  }) => Promise<any>;
  TokenVerification: ({ token }: { token: string }) => Promise<any>;
  logout: () => Promise<void>;
  updateLoginRes: (res: any) => Promise<any>;
}

export const initialState: UserState = {
  userType: "INIT",
  error: undefined,
  accessToken: undefined,
  OTPLogin: async () => {},
  OTPVerification: async () => {},
  TokenVerification: async () => {},
  logout: async () => {},
  updateLoginRes: async () => {},
};

export const AuthContext = createContext(initialState);

const tokenVerificationAction: any = (formValues: { token: string }) => ({
  method: "GET",
  endpoint: `/user/authinticate/user?token=${formValues.token}`,
});
const otpVerificationAction: any = (formValues: {
  mobile: string;
  otp: string;
}) => ({
  method: "POST",
  endpoint: `/user/auth/client/otp/verify?mobile=${formValues.mobile}&otp=${formValues.otp}`,
  body: {},
});

const otpLoginAction: any = (formValues: { mobile: string }) => ({
  method: "POST",
  endpoint: `/user/auth/client/otp/send?mobile=${formValues.mobile}`,
  body: {},
});

export const AuthContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  // const [currentAccessToken, setCurrentAccessToken] = useState("");
  const { mutate: otpLoginMutate } = useMutation(otpLoginAction);
  const { mutate: otpVerificationMutate } = useMutation(otpVerificationAction);
  const { mutate: tokenVerificationMutate } = useMutation(
    tokenVerificationAction
  );

  const controlReducer = (_state: UserState, action: any): UserState => {
    switch (action.name) {
      case "LOG_IN":
        return {
          ..._state,
          userType: "LOGGED_IN",
          error: undefined,
          accessToken: action.accessToken,
          accountProfile: action.accountProfile,
        };
      case "LOG_IN_ERROR":
        return {
          ..._state,
          userType: "INIT",
          error: action.value,
        };
      case "LOG_OUT": {
        return {
          ..._state,
          userType: "NOT_LOGGED_IN",
          accessToken: "",
          error: undefined,
        };
      }
      case "UPDATE_ACCOUNT_PROFILE": {
        return {
          ..._state,
          accountProfile: action.accountProfile,
        };
      }
      default:
        return {
          ..._state,
        };
    }
  };

  const [state, dispatch] = useReducer(controlReducer, initialState);

  useEffect(() => {
    const authCheck = async () => {
      try {
        const tokenData = await getItem("trinkerr.token");
        if (tokenData !== undefined && tokenData !== null) {
          const tokenObj = JSON.parse(tokenData);
          await doLogin(tokenObj);
        } else {
          await doLogout();
        }
      } catch {
        await doLogout();
      }
    };
    authCheck();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doLogin = async (response: any) => {
    if (response) {
      const accessToken = response.auth_token;
      // const accessToken = response.token;
      const payload = JSON.stringify(response);
      setItem("trinkerr.token", payload);
      const accountProfile = {
        client_inv19_id: response.client_inv19_id,
        user_id: response.client_inv19_id,
        client_mobile: response.client_mobile,
      };

      dispatch({
        name: "LOG_IN",
        accessToken: accessToken,
        accountProfile: accountProfile,
      });
    }
  };

  const doLogout = useCallback(async () => {
    const tokenVal = await getItem("trinkerr.token");
    await setItem("trinkerr.token", "");
    await setItem("trinkerr.tempIPO", "");
    dispatch({ name: "LOG_OUT" });
    if (tokenVal !== undefined && tokenVal !== null && tokenVal !== "") {
      console.log('tokenVal', tokenVal)
      window.location.reload();
    }
  }, []);

  const authActions = React.useMemo(
    () => ({
      updateLoginRes: async (loginRes: any) => {
        await doLogin(loginRes);
        window.location.reload();
      },
      OTPLogin: async ({ phoneNumber }: { phoneNumber: string }) => {
        const { error: mutateError, payload: otpLoginPayload } =
          await otpLoginMutate({ mobile: phoneNumber });
        if (mutateError) {
          // eslint-disable-next-line
          throw "something went wrong!";
        }
        if (isSuccess(otpLoginPayload)) {
          return successData(otpLoginPayload);
        } else {
          // eslint-disable-next-line
          throw errorMessage(otpLoginPayload);
        }
      },
      OTPVerification: async ({
        phoneNumber,
        otpCode,
      }: {
        phoneNumber: string;
        otpCode: string;
      }) => {
        const { error: mutateError, payload: otpVerifiactionPayload } =
          await otpVerificationMutate({ mobile: phoneNumber, otp: otpCode });
        if (mutateError) {
          // eslint-disable-next-line
          throw "something went wrong!";
        }
        if (isSuccess(otpVerifiactionPayload)) {
          const resData = successData(otpVerifiactionPayload);
          if (resData.length > 0) {
            await doLogin(resData[0]);
          } else {
            throw "something went wrong!";
          }
        } else {
          // eslint-disable-next-line
          throw errorMessage(otpVerifiactionPayload);
        }
      },
      TokenVerification: async ({ token }: { token: string }) => {
        const { error: mutateError, payload: tokenVerifiactionPayload } =
          await tokenVerificationMutate({ token: token });
        if (mutateError) {
          // eslint-disable-next-line
          throw "something went wrong!";
        }
        if (isSuccess(tokenVerifiactionPayload)) {
          const resData = successData(tokenVerifiactionPayload);
          if (resData && resData.token) {
            await doLogin(resData);
          } else {
            throw "something went wrong!";
          }
        } else {
          // eslint-disable-next-line
          throw errorMessage(tokenVerifiactionPayload);
        }
      },
      logout: async () => {
        await doLogout();
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <AuthContext.Provider
      value={{
        ...state,
        ...authActions,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within a AuthContext");
  }
  return context;
};
